/**
 * Container for a single watchlist row in the SelectListSheet.
 *
 * Wraps the toggle callback to avoid inline functions in the parent View.
 * @module features/scouting/components/SelectListRow/SelectListRowContainer
 */

import React, { useCallback } from "react";

import SelectListRowView from "./SelectListRowView";

/** Props for SelectListRowContainer */
interface SelectListRowContainerProps {
  /** Watchlist ID */
  readonly id: string;
  /** Watchlist display name */
  readonly name: string;
  /** Number of players in this watchlist */
  readonly playerCount: number;
  /** Whether this watchlist is currently checked */
  readonly isChecked: boolean;
  /** Callback when this row is toggled */
  readonly onToggle: (watchlistId: string) => void;
  /** Callback when share icon is pressed (hidden when absent) */
  readonly onShare?: () => void;
  /** Callback when edit icon is pressed (hidden when absent) */
  readonly onEdit?: () => void;
  /** Callback when delete icon is pressed (hidden when absent) */
  readonly onDelete?: () => void;
}

/**
 * Container for a single watchlist checkbox row.
 * @param root0 - Container props
 * @param root0.id - Watchlist ID
 * @param root0.name - Watchlist name
 * @param root0.playerCount - Player count
 * @param root0.isChecked - Whether checked
 * @param root0.onToggle - Toggle handler
 * @param root0.onShare - Share handler
 * @param root0.onEdit - Edit handler
 * @param root0.onDelete - Delete handler
 */
const SelectListRowContainer = ({
  id,
  name,
  playerCount,
  isChecked,
  onToggle,
  onShare,
  onEdit,
  onDelete,
}: SelectListRowContainerProps) => {
  const handleToggle = useCallback(() => {
    onToggle(id);
  }, [onToggle, id]);

  return (
    <SelectListRowView
      id={id}
      name={name}
      playerCount={playerCount}
      isChecked={isChecked}
      onToggle={handleToggle}
      onShare={onShare}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
};

SelectListRowContainer.displayName = "SelectListRowContainer";

export default SelectListRowContainer;
